// AR Wave Timeline — a per-day breakdown of attempt activity for one
// cohort × publication. Each day-row shows:
//   - calendar date + day-offset from cohort_month
//   - SC schedule wave label (when known: A1, fast-retry +22h, A2, etc.)
//   - total attempts that day
//   - per-status mix (CHARGED / CHARGE FAILED / FIRST CHARGE FAILED / etc.)
//   - retry-path split (fast / standard / null=success)
//   - per-attempt-seq breakdown (A1 / A2 / A3 / ...)
//
// Operators can click any day to drill into the per-attempt list filtered
// to that exact date.

import { Link } from 'react-router-dom'
import type { ARCohortTimeline } from '../api/client'
import { Hint, StatusBadge } from './ARGlossary'

function fmtDateShort(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function fmtDOW(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' })
}

export function ARWaveTimeline({
  data,
  monthParam,
  publication,
}: {
  data: ARCohortTimeline
  monthParam: string  // YYYY-MM (URL slug, no day)
  publication: string
}) {
  if (data.days.length === 0) {
    return (
      <div className="border border-ink-500 bg-ink-800/40 p-6 text-paper-trace label">
        No AR activity recorded for this cohort yet. The first attempt-1 wave
        fires on Day +3 from cohort_month (= Day +2 from SC's processing day).
      </div>
    )
  }

  // Sort statuses for stable column order in the bar
  const allStatuses = Array.from(
    new Set(data.days.flatMap((d) => Object.keys(d.by_status))),
  )
  const statusOrder = ['CHARGED', 'CARD UPDATED', 'CHARGE FAILED', 'FIRST CHARGE FAILED', 'RECHARGE FAILED', 'THIRD CHARGE FAILED', 'FOURTH CHARGE FAILED', 'FIFTH CHARGE FAILED']
  allStatuses.sort((a, b) => {
    const ai = statusOrder.indexOf(a)
    const bi = statusOrder.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })

  return (
    <div className="border border-ink-500 bg-ink-800/40 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-ink-500/60 bg-ink-800/60 flex items-baseline justify-between">
        <div className="label-amber">Daily attempt waves</div>
        <Hint
          title="Wave timeline"
          short="Day-by-day breakdown of every AR attempt SC has fired for this cohort."
          detail="Each row is one calendar day of attempts. The wave label calls out which scheduled wave per the +2/+5/+3/+8/+6 cadence (anchored on SC processing day; cohort offsets are 1 day later because cohort_month tracks the physical pull). Click any day to drill into the per-attempt list filtered to that date."
        >
          <span className="label text-paper-trace">what is this?</span>
        </Hint>
      </div>
      <table className="w-full text-[12px] font-mono">
        <thead>
          <tr className="border-b border-ink-500/40 bg-ink-800/40">
            <th className="text-left px-3 py-2 label font-normal">Date</th>
            <th className="text-left px-3 py-2 label font-normal">Wave</th>
            <th className="text-right px-3 py-2 label font-normal">Total</th>
            <th className="text-left px-3 py-2 label font-normal">Status mix</th>
            <th className="text-left px-3 py-2 label font-normal">Retry path</th>
            <th className="text-left px-3 py-2 label font-normal">Attempt #</th>
          </tr>
        </thead>
        <tbody>
          {data.days.map((day) => (
            <tr key={day.date} className="border-b border-ink-500/20 hover:bg-ink-700/30 transition-colors">
              <td className="px-3 py-2 align-top whitespace-nowrap">
                <Link
                  to={`/ar-report/${monthParam}/${publication}/attempts?on_date=${day.date}`}
                  className="text-paper hover:text-amber-glow"
                >
                  <div>{fmtDateShort(day.date)}</div>
                  <div className="text-paper-trace text-[10px]">
                    {fmtDOW(day.date)} · D+{day.day_offset}
                  </div>
                </Link>
              </td>
              <td className="px-3 py-2 align-top">
                {day.wave_label ? (
                  <span className="text-amber-glow text-[11px]">{day.wave_label}</span>
                ) : (
                  <span className="text-paper-trace text-[11px]">—</span>
                )}
              </td>
              <td className="px-3 py-2 align-top text-right tabular-nums text-paper text-[14px]">
                {day.total}
              </td>
              <td className="px-3 py-2 align-top">
                <div className="flex flex-wrap gap-1.5">
                  {allStatuses
                    .filter((st) => day.by_status[st] > 0)
                    .map((st) => (
                      <span key={st} className="inline-flex items-center gap-1">
                        <StatusBadge status={st} />
                        <span className="tabular-nums text-paper-dim text-[11px]">×{day.by_status[st]}</span>
                      </span>
                    ))}
                </div>
              </td>
              <td className="px-3 py-2 align-top">
                <div className="flex flex-wrap gap-1.5">
                  {day.by_retry_path.fast > 0 && (
                    <Hint section="retry_paths" termKey="fast" underline="none">
                      <span className="text-radar-cyan tabular-nums">⚡{day.by_retry_path.fast}</span>
                    </Hint>
                  )}
                  {day.by_retry_path.standard > 0 && (
                    <Hint section="retry_paths" termKey="standard" underline="none">
                      <span className="text-paper-dim tabular-nums">std·{day.by_retry_path.standard}</span>
                    </Hint>
                  )}
                  {day.by_retry_path.null > 0 && (
                    <Hint
                      title="Successes"
                      short="Charged or CARD UPDATED — no retry path needed."
                      detail="retry_path is NULL on non-failure rows; these are CHARGED (renewals) or CARD UPDATED (customer updated their card)."
                      underline="none"
                    >
                      <span className="text-radar-green tabular-nums">✓{day.by_retry_path.null}</span>
                    </Hint>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 align-top">
                <div className="flex flex-wrap gap-1.5 tabular-nums">
                  {Object.entries(day.by_attempt_seq).map(([seq, n]) => (
                    <span key={seq} className="text-paper-dim text-[11px]">
                      {seq}<span className="text-paper-trace">·{n}</span>
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
